import { PartyPopper } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useVoiceRoom } from '../../contexts/VoiceRoomContext';
import { SOUNDBOARD, preloadSoundboard } from '../../lib/soundboard';
import { Popover } from '../tasks/linear/Popover';

/**
 * The soundboard: a button beside the microphone that opens the joke sounds
 * anyone in the call can fire. Pressing one plays it for the whole room, not
 * just for the person pressing — the press travels on the LiveKit data channel
 * and every client synthesises the sound locally (see `lib/soundboard.ts`).
 *
 * Deliberately a popover rather than six buttons in the header: the control
 * bar already carries share / mic / leave, and a row of emoji beside a red
 * "Leave" button is a misclick waiting to happen.
 */
export function SoundboardButton({ className }: { className: string }) {
  const { t } = useI18n();
  const room = useVoiceRoom();

  return (
    <Popover
      width={208}
      align="end"
      trigger={({ open, toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={() => {
            preloadSoundboard();
            toggle();
          }}
          aria-haspopup="menu"
          aria-expanded={open}
          title={t('teamChat.soundboard')}
          className={`${className} text-muted-foreground hover:bg-accent hover:text-foreground`}
        >
          <PartyPopper size={16} />
        </button>
      )}
    >
      {() => (
        <div className="grid grid-cols-3 gap-1 p-1.5">
          {SOUNDBOARD.map((sound) => (
            <button
              key={sound.id}
              type="button"
              // The popover stays open: half the point is firing two in a row.
              onClick={() => room.playSoundboard(sound.id)}
              title={t(`teamChat.soundboard.${sound.id}`)}
              className="flex flex-col items-center gap-1 rounded-md px-1 py-2 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <span aria-hidden className="text-lg leading-none">
                {sound.emoji}
              </span>
              <span className="w-full truncate text-center">
                {t(`teamChat.soundboard.${sound.id}`)}
              </span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}
